import { createFileRoute } from "@tanstack/react-router";
import { trpc } from "@/app/api/trpcClient";

export const Route = createFileRoute("/test")({
  component: TestComponent,
});

function TestComponent() {
  const {
    data: teamTaskViews,
    isLoading: teamTaskViewsLoading,
    error: teamTaskViewsError,
  } = trpc.teamTaskViews.list.useQuery();

  const {
    data: people,
    isLoading: peopleLoading,
    error: peopleError,
  } = trpc.people.list.useQuery();

  const {
    data: projects,
    isLoading: projectsLoading,
    error: projectsError,
  } = trpc.projects.list.useQuery();

  const { data: tasks, isLoading: tasksLoading, error: tasksError } = trpc.tasks.list.useQuery();

  const {
    data: scopes,
    isLoading: scopesLoading,
    error: scopesError,
  } = trpc.scopes.list.useQuery();

  const isLoading =
    teamTaskViewsLoading || peopleLoading || projectsLoading || tasksLoading || scopesLoading;
  const hasError = teamTaskViewsError ?? peopleError ?? projectsError ?? tasksError ?? scopesError;

  if (isLoading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h3 className="text-blue-800 font-medium">Testing tRPC Integration</h3>
        <div className="flex items-center gap-2 mt-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <p className="text-blue-600 text-sm">Loading data from all endpoints...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <h3 className="text-red-800 font-medium">tRPC Integration Test Failed</h3>
        <div className="mt-2 text-sm text-red-600 space-y-1">
          {teamTaskViewsError && <span>Team Task Views Error: {teamTaskViewsError.message}</span>}
          {peopleError && <span>People Error: {peopleError.message}</span>}
          {projectsError && <span>Projects Error: {projectsError.message}</span>}
          {tasksError && <span>Tasks Error: {tasksError.message}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-green-50 border border-green-200 rounded-md p-4">
      <h3 className="text-green-800 font-medium">✅ tRPC Integration Test Successful!</h3>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="bg-white rounded p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Team Task Views</div>
          <div className="text-lg font-semibold text-green-600">{teamTaskViews?.length ?? 0}</div>
        </div>
        <div className="bg-white rounded p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">People</div>
          <div className="text-lg font-semibold text-blue-600">{people?.length ?? 0}</div>
        </div>
        <div className="bg-white rounded p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Projects</div>
          <div className="text-lg font-semibold text-purple-600">{projects?.length ?? 0}</div>
        </div>
        <div className="bg-white rounded p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Tasks</div>
          <div className="text-lg font-semibold text-orange-600">{tasks?.length ?? 0}</div>
        </div>
        <div className="bg-white rounded p-3 border">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Scopes</div>
          <div className="text-lg font-semibold text-orange-600">{scopes?.length ?? 0}</div>
        </div>
      </div>
      <p className="text-green-600 text-xs mt-3">
        All tRPC endpoints are responding correctly with type-safe data!
      </p>
    </div>
  );
}
